import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EventChip from './EventChip';
import { MAX_VISIBLE_EVENTS } from './constants';

/** Approximate chip + gap height used until measured. */
const ESTIMATED_CHIP_HEIGHT = 22;
const ESTIMATED_MORE_HEIGHT = 18;
const ESTIMATED_GAP = 4;

function computeVisibleCount(availablePx, eventCount, chipH, moreH, gap) {
  if (eventCount <= 0 || availablePx <= 0) return 0;

  const allHeight = eventCount * chipH + Math.max(0, eventCount - 1) * gap;
  if (allHeight <= availablePx && eventCount <= MAX_VISIBLE_EVENTS) {
    return eventCount;
  }

  let maxByHeight = 0;
  let used = moreH;
  while (maxByHeight < eventCount && maxByHeight < MAX_VISIBLE_EVENTS) {
    const next = used + (maxByHeight > 0 ? gap : 0) + chipH;
    if (next > availablePx) break;
    used = next;
    maxByHeight += 1;
  }
  return Math.max(0, maxByHeight);
}

export default function MonthDayCell({
  cell,
  events,
  isLastCol,
  isLastRow,
  dragOver,
  draggingEventId,
  readOnly = false,
  onDayClick,
  onEventClick,
  onEventContextMenu,
  onEventDragStart,
  onEventDragEnd,
  onMoreClick,
  onDragOver,
  onDragLeave,
  onDrop,
}) {
  const { t } = useTranslation('planning');
  const borderR = isLastCol ? '' : 'border-r ';
  const borderB = isLastRow ? '' : 'border-b ';
  const eventsRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(() =>
    Math.min(events.length, MAX_VISIBLE_EVENTS)
  );

  const recalc = useCallback(() => {
    const container = eventsRef.current;
    if (!container) return;
    const available = container.clientHeight;
    const chipEl = container.querySelector('.month-event-chip');
    const moreEl = container.querySelector('.month-more');
    const chipH = chipEl?.offsetHeight || ESTIMATED_CHIP_HEIGHT;
    const moreH = moreEl?.offsetHeight || ESTIMATED_MORE_HEIGHT;
    const styles = window.getComputedStyle(container);
    const gap = Number.parseFloat(styles.rowGap || styles.gap) || ESTIMATED_GAP;
    setVisibleCount(computeVisibleCount(available, events.length, chipH, moreH, gap));
  }, [events.length]);

  useLayoutEffect(() => {
    recalc();
  }, [recalc, cell.iso, events]);

  useEffect(() => {
    const container = eventsRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      recalc();
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [recalc]);

  const visible = events.slice(0, visibleCount);
  const overflow = Math.max(0, events.length - visibleCount);

  const baseClass = cell.outside
    ? `month-day p-2 ${borderR}${borderB}border-border bg-background-light opacity-40${
        dragOver ? ' drag-over' : ''
      }`
    : `month-day p-2 ${borderR}${borderB}border-border transition-colors hover:bg-background-light/50 cursor-pointer ${
        cell.today ? 'bg-primary/5' : 'bg-surface'
      }${dragOver ? ' drag-over' : ''}`;

  return (
    <div
      className={baseClass}
      data-date={cell.iso}
      data-outside={cell.outside ? 'true' : undefined}
      onClick={(e) => {
        if (cell.outside || readOnly) return;
        if (e.target.closest('.month-event-chip') || e.target.closest('.month-more')) return;
        onDayClick?.(cell);
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {cell.today && !cell.outside ? (
        <div className="month-day-num flex justify-end shrink-0">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white text-[11px] font-semibold">
            {cell.dayNum}
          </span>
        </div>
      ) : (
        <span className="month-day-num block text-right text-[11px] font-semibold text-text-secondary shrink-0">
          {cell.dayNum}
        </span>
      )}

      <div ref={eventsRef} className="month-day-events">
        {visible.map((ev) => (
          <EventChip
            key={ev.segmentKey || ev.id}
            event={ev}
            isDragging={String(draggingEventId) === String(ev.id)}
            draggable={!readOnly}
            onClick={(e) => {
              e.stopPropagation();
              onEventClick?.(ev, cell);
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onEventContextMenu?.(ev, e.clientX, e.clientY);
            }}
            onDragStart={(e) => onEventDragStart?.(ev, e, false)}
            onDragEnd={onEventDragEnd}
          />
        ))}
        {overflow > 0 && (
          <button
            type="button"
            className="month-more"
            onClick={(e) => {
              e.stopPropagation();
              onMoreClick?.(cell, e.currentTarget);
            }}
          >
            {t('moreEvents', { count: overflow })}
          </button>
        )}
      </div>
    </div>
  );
}
